import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  getUpdateState, hasUnsavedWork, readDocumentUpdateNoticeMode, setUpdateState, shouldShowNotice,
  subscribeUpdateState, watchAppBuild, UPDATE_NOTICE_MODE_ATTRIBUTE,
} from '@/lib/appUpdates';
import { normalizeLanguage, t } from '@/lib/i18n';
import { useToastClearanceContributor } from '@/lib/toastPosition';

const DISMISSED_KEY = 'lm.app-update.dismissed-build';
function readDismissed(): string | null {
  try { return window.localStorage.getItem(DISMISSED_KEY); } catch { return null; }
}
function writeDismissed(build: string): void {
  try { window.localStorage.setItem(DISMISSED_KEY, build); } catch { /* per-tab state still applies */ }
}

/** Re-checks the page, unsaved work and elapsed time this often. */
const TICK_MS = 15_000;

/**
 * Outside route/error boundaries so an update remains reachable if a route
 * fails. It watches for new releases; whether the card shows is decided by
 * `shouldShowNotice` (never on an editor or over unsaved work, "Later" hides
 * it until the next release, Quiet users only see it for a critical or
 * day-old release). The quiet switch between pages lives in AppUpdateSync.
 */
export function AppUpdateNotice({ buildId = import.meta.env.VITE_BUILD_ID }: { buildId?: string } = {}) {
  const state = useSyncExternalStore(subscribeUpdateState, getUpdateState);
  const [language, setLanguage] = useState(document.documentElement.lang);
  const [mode, setMode] = useState(readDocumentUpdateNoticeMode);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [page, setPage] = useState(() => ({ path: window.location.pathname, since: Date.now() }));
  const [now, setNow] = useState(() => Date.now());
  const [unsaved, setUnsaved] = useState(hasUnsavedWork);

  useEffect(() => {
    // Build commands emit metadata even when NODE_ENV is overridden locally.
    // Dev servers have an empty build ID and no version endpoint.
    if (!buildId) return;
    const stop = watchAppBuild(buildId, (available, info) => {
      setUpdateState({ build: available ? info?.buildId ?? 'unknown' : null, critical: info?.critical });
    });
    return () => {
      stop();
      setUpdateState({ build: null });
    };
  }, [buildId]);
  useEffect(() => {
    // Public pages set html.lang within their own LanguageProvider; the
    // signed-in user's preference arrives on html[data-update-notices].
    const observer = new MutationObserver(() => {
      setLanguage(document.documentElement.lang);
      setMode(readDocumentUpdateNoticeMode());
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['lang', UPDATE_NOTICE_MODE_ATTRIBUTE] });
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!state.build) return;
    const tick = () => {
      const path = window.location.pathname;
      setPage((prev) => (prev.path === path ? prev : { path, since: Date.now() }));
      setUnsaved(hasUnsavedWork());
      setNow(Date.now());
    };
    tick();
    const timer = window.setInterval(tick, TICK_MS);
    window.addEventListener('popstate', tick);
    window.addEventListener('focus', tick);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('popstate', tick);
      window.removeEventListener('focus', tick);
    };
  }, [state.build]);

  const visible = shouldShowNotice({
    state, now, mode, pathname: page.path, unsaved, dismissedBuild: dismissed, pageSince: page.since,
  });
  // The notice runs along the bottom edge; toasts start above it while it shows.
  useToastClearanceContributor('app-update-notice', visible ? { bottom: 128 } : null);
  if (!visible || !state.build) return null;
  const build = state.build;
  const lang = normalizeLanguage(language.split('-')[0]);
  return (
    <aside role="status" aria-live="polite" className="fixed bottom-4 left-4 right-4 z-[100] rounded-2xl border border-border bg-background p-4 text-foreground shadow-xl sm:left-auto sm:w-80">
      <p className="text-sm font-semibold">{t(lang, 'common.app_update.title')}</p>
      <p className="mt-1 text-sm text-muted-foreground">{t(lang, 'common.app_update.body')}</p>
      <div className="mt-3 flex items-center gap-2">
        <button type="button" onClick={() => window.location.reload()} className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
          {t(lang, 'common.app_update.refresh')}
        </button>
        <button
          type="button"
          onClick={() => { writeDismissed(build); setDismissed(build); }}
          className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {t(lang, 'common.app_update.later')}
        </button>
      </div>
    </aside>
  );
}
